import React, { useContext, useState, useEffect, useRef } from 'react'
import { PublicIndexContext } from '../context/PublicIndexContext'
import BankID from '../services/api/BankID'
import { ClipLoader } from 'react-spinners'
import PowerOfAttorney from './PowerOfAttorney'
import SignedPowerOfAttorney from './SignedPowerOfAttorney'
import DigitalRegistrationSigning from '../services/api/DigitalRegistrationSigning'
import { publicIpv4 } from 'public-ip'
import PropTypes from 'prop-types'
import { BankIdSigningActions } from '../helpers/Constants.js'
import BankIDQRCode from './BankIDQRCode'
import { useTranslation } from 'react-i18next'

const AbortController = window.AbortController

const ErrorMessage = ({ message }) => {
  return (
    <p>{message}</p>
  )
}

const ResponseMessage = ({ bankIDCollectResponse, bankIDSignResponse, error }) => {
  const { t } = useTranslation()
  let response = ''

  if (error) {
    response = t('BankId something went wrong message')
  }

  if (bankIDSignResponse.errorCode) {
    response = t(decodeURIComponent(bankIDSignResponse.errorUserMessage))
  } else {
    switch (bankIDCollectResponse.status) {
    case 'failed':
      response = t(decodeURIComponent(bankIDCollectResponse.hintCodeUserMessage))
      break
    case 'pending':
      response = t(decodeURIComponent(bankIDCollectResponse.hintCodeUserMessage))
      break
    default:
      response = ''
      break
    }
  }

  return (<p>{response}</p>)
}

const BankIDSign = ({ signAction }) => {
  const abortController = new AbortController()
  const publicIndexContext = useContext(PublicIndexContext)

  const [error, setError] = useState({})
  const [loading, setLoading] = useState(true)
  const [bankIDSignResponse, setBankIDSignResponse] = useState({})
  const [bankIDCollectResponse, setBankIDCollectResponse] = useState({})
  const [aborted, setAborted] = useState(false)
  const [isPowerOfAttorneySigned, setIsPowerOfAttorneySigned] = useState(false)
  const [useQRCode, setUseQRCode] = useState(false)
  const [ipAddress, setIpAddress] = useState(null)

  const isSigningInProccess = useRef(false)

  useEffect(() => {
    if (Object.keys(bankIDSignResponse).length === 0) {
      window.scrollTo(0, 0)
      sign()
    }

    return () => null
  }, [])

  async function sign () {
    const bankID = new BankID()

    publicIpv4().then(ip => {
      setIpAddress(ip)
      return ip
    }).then(ip => {
      bankID.post(
        publicIndexContext.customerId,
        'sign',
        ip,
        publicIndexContext.textToSignWithBankID,
        abortController.signal
      ).then(response => {
        if (response.status !== 200) {
          throw new Error(response.status)
        }
        return response.json()
      }).then(data => {
        setBankIDSignResponse(data)
      }).catch(error => {
        setError(error)
        setLoading(false)
      })
    }).catch(error => {
      throw new Error('Could not get public ip, try disable any ad-blockers', error)
    })
  }

  useEffect(() => {
    if (shouldCollectBeCancelled()) {
      return () => null
    }

    let interval = 0
    if (
      loading &&
        bankIDSignResponse &&
        !bankIDSignResponse.errorCode &&
        ['complete', 'failed'].indexOf(bankIDCollectResponse.status) === -1
    ) {
      selectSigningMethod(signAction)

      interval = setInterval(() => {
        if (Object.hasOwn(bankIDSignResponse, 'orderRef') && bankIDSignResponse.orderRef !== null) {
          collect(bankIDSignResponse.orderRef)
        }
      }, 1500)
    }

    if (shouldCollectBeCancelled()) {
      return () => {
        clearInterval(interval)
        return null
      }
    } else {
      return () => clearInterval(interval)
    }
  }, [bankIDSignResponse, bankIDCollectResponse])

  useEffect(() => {
    if (bankIDCollectResponse.status ===
      'complete' && !isPowerOfAttorneySigned) {
      const digitalregistrationSigning = new DigitalRegistrationSigning()
      digitalregistrationSigning.post(
        publicIndexContext.siteId,
        bankIDCollectResponse.bankIdLogId,
        publicIndexContext.unSignedPowerOfAttorney.id
      )
        .then(response => {
          if (response.status !== 201) {
            throw new Error(response.status)
          }
          return response.json()
        })
        .then(data => {
          setIsPowerOfAttorneySigned(true)
        })
        .catch(error => {
          console.log(error)
        })
    }
  }, [bankIDCollectResponse])

  const selectSigningMethod = (signAction) => {
    if (!isSigningInProccess.current && bankIDSignResponse.orderRef !== undefined) {
      switch (signAction) {
      case BankIdSigningActions.MOBILE:
        window.location = `bankid:///?autostarttoken=${bankIDSignResponse.autoStartToken}&redirect=null`
        isSigningInProccess.current = true
        break
      case BankIdSigningActions.QR_CODE:
        isSigningInProccess.current = true
        setUseQRCode(true)
        break
      default:
        console.log('Something went wrong, please try again.')
        break
      }
    }
  }

  const shouldCollectBeCancelled = () => {
    if (
      Object.keys(bankIDCollectResponse).includes('status') &&
      ['complete', 'failed'].indexOf(bankIDCollectResponse.status) !== -1
    ) {
      setLoading(false)
      return true
    }

    return false
  }

  async function collect (orderRef) {
    const bankID = new BankID()
    bankID.post(
      publicIndexContext.customerId,
      'collect',
      ipAddress,
      null,
      orderRef,
      abortController.signal
    )
      .then(response => {
        if (response.status !== 200) {
          throw new Error(response.status)
        }
        return response.json()
      })
      .then(data => {
        if (data.status === 'failed') {
          setLoading(false)
        }
        setBankIDCollectResponse(data)
      })
      .catch(error => {
        setError(error)
        setLoading(false)
      })
  }

  async function cancelSign () {
    const bankID = new BankID()

    if (
      Object.keys(bankIDSignResponse).length > 0 &&
        bankIDSignResponse.orderRef !== null
    ) {
      bankID.cancel(
        publicIndexContext.customerId,
        bankIDSignResponse.orderRef,
        abortController.signal
      )
        .then(res => res.json())
        .then(() => {
          setLoading(false)
          setUseQRCode(false)
          setAborted(true)
          abortController.abort('User aborted sign')
        })
        .catch(error => {
          setUseQRCode(false)
          setLoading(false)
          setError(error)
          setAborted(true)
          abortController.abort()
        })
    }
  }

  const CancelButton = () => {
    return (
      <button
        style={publicIndexContext.style.button}
        onClick={() => { cancelSign() }}
        className="btn btn-primary custom-btn">
        Avbryt
      </button>
    )
  }

  if (aborted) {
    return <PowerOfAttorney />
  }

  if (isPowerOfAttorneySigned) {
    return <SignedPowerOfAttorney bankIDCollectResponse={bankIDCollectResponse} />
  }

  const isCollectStatusDone = () => {
    return (Object.keys(bankIDCollectResponse).includes('status') &&
    ['complete', 'failed'].indexOf(bankIDCollectResponse.status) !== -1)
  }

  const isCollectStatusFailed = () => {
    return (Object.keys(bankIDCollectResponse).includes('status') &&
    ['failed'].indexOf(bankIDCollectResponse.status) !== -1)
  }

  if (isSigningInProccess.current === true && !isCollectStatusDone()) {
    return (
      <div>
        {(loading && !useQRCode)
          ? <div className="row mt-5">
            <div className="col-12 text-center">
              <div className='sweet-loading'>
                <ClipLoader
                  size={75}
                  color={'#BF33BD'}
                  loading={loading}
                />
              </div>
            </div>
            <div className="row mt-3" style={{ marginBottom: '1em' }}>
              <div className="col-12 text-center">
                <CancelButton />
              </div>
            </div>
          </div>
          : null
        }
        <div className="row mt-3">
          <div className="col-12 text-center">
            <span className="paragraph-regular-16">
              <ResponseMessage
                error={error}
                bankIDSignResponse={bankIDSignResponse}
                bankIDCollectResponse={bankIDCollectResponse} />
            </span>
          </div>
        </div>
        {(loading && useQRCode)
          ? <div className="row mt-5 justify-content-md-center">
            <div className="col-12 text-center">
              <BankIDQRCode
                orderRef={bankIDSignResponse.orderRef}
                show={useQRCode}
                onCancel={() => cancelSign()} />
            </div>
            <div className="col-12 text-center" style={{ marginBottom: '1em' }}>
              <CancelButton />
            </div>
          </div>
          : null
        }
      </div>
    )
  } if (isCollectStatusFailed()) {
    return (
      <div>
        <div className="row mt-3">
          <div className="col-12 text-center">
            <span className="paragraph-regular-16">
              <ResponseMessage
                error={error}
                bankIDSignResponse={bankIDSignResponse}
                bankIDCollectResponse={bankIDCollectResponse} />
            </span>
          </div>
        </div>
        <div className="col-12 text-center" style={{ marginBottom: '1em' }}>
          <CancelButton />
        </div>
      </div>
    )
  }
}

BankIDSign.propTypes = {
  signAction: PropTypes.number
}

ResponseMessage.propTypes = {
  bankIDCollectResponse: PropTypes.object.isRequired,
  bankIDSignResponse: PropTypes.object.isRequired,
  error: PropTypes.object
}

ErrorMessage.propTypes = {
  message: PropTypes.string
}

export default BankIDSign
